import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";

interface DefectSystem {
  system: string;
  percent: number;
  color: string;
  items: { name: string; description: string }[];
}

interface DetailedDefectsProps {
  title: string;
  subtitle: string;
  defects: DefectSystem[];
}

export const DetailedDefects = ({ title, subtitle, defects }: DetailedDefectsProps) => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
            {title}
          </h2>
          <p className="font-body text-muted-foreground max-w-2xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {defects.map((defect, index) => (
            <motion.div
              key={defect.system}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <h3 className="font-heading font-bold text-autosur-navy text-sm">{defect.system}</h3>
                </div>
                <span className="font-heading font-bold text-autosur-red text-lg">{defect.percent}%</span>
              </div>
              <div className={`w-full bg-gray-100 rounded-full h-2 mb-4`}>
                <div className={`${defect.color} rounded-full h-2`} style={{ width: `${defect.percent}%` }} />
              </div>
              <ul className="space-y-2">
                {defect.items.map((item) => (
                  <li key={item.name} className="font-body text-sm">
                    <span className="font-semibold text-autosur-navy">{item.name}</span>
                    <span className="text-muted-foreground"> : {item.description}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
